<template>
  <ClMotion
    v-if="hasActiveJob"
    preset="slideUp"
    class="job-progress-card font-sans text-foreground"
    :class="{ 'minimized': isMinimized }"
    :style="cardStyle"
  >
    <div class="card-header" @mousedown="startDrag">
      <div class="d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <div v-if="hasActiveRunningJob" class="spinner-border spinner-border-sm text-light me-2" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <i v-else class="fas fa-tasks me-2"></i>
          <span class="fw-bold">Job Progress</span>
          <span v-if="isMinimized && hasActiveRunningJob" class="ms-2 text-light fw-normal">
            {{ Math.round(overallPercent) }}%
          </span>
          <span v-if="!isMinimized" class="badge bg-primary ms-2">{{ jobCount }}</span>
        </div>
        <div class="card-controls">
          <button
            class="btn btn-sm btn-link text-dark p-1"
            @click="toggleMinimize"
            :title="isMinimized ? 'Expand' : 'Minimize'"
          >
            <i :class="isMinimized ? 'fas fa-window-maximize' : 'fas fa-window-minimize'"></i>
          </button>
        </div>
      </div>
    </div>

    <div v-if="!isMinimized" class="card-body">
      <div v-for="job in activeJobs" :key="job.id" class="job-item mb-3">
        <div class="d-flex justify-content-between align-items-start mb-2">
          <div>
            <strong class="text-truncate d-block" style="max-width: 200px;">{{ job.businessName || 'Business' }}</strong>
            <small class="text-muted">{{ job.jobTypeName || job.jobTypeCode || 'Job' }} • {{ job.status }}</small>
          </div>
          <button
            class="btn btn-sm btn-link text-danger p-0"
            @click="removeJob(job.id)"
            title="Remove"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="progress mb-2" style="height: 20px;">
          <div
            class="progress-bar"
            :class="{
              'bg-success': job.status === 'succeeded',
              'bg-danger': job.status === 'failed',
              'bg-warning': job.status === 'cancelled',
              'progress-bar-striped progress-bar-animated': ['queued', 'running'].includes(job.status)
            }"
            role="progressbar"
            :style="{ width: getJobPercent(job) + '%' }"
          >
            {{ job.processed }} / {{ job.total }} ({{ Math.round(getJobPercent(job)) }}%)
          </div>
        </div>

        <div class="d-flex justify-content-between small text-muted">
          <span>Success: {{ job.successCount }}</span>
          <span>Failed: {{ job.failCount }}</span>
          <span>{{ Math.round(getJobPercent(job)) }}%</span>
        </div>

        <div
          v-if="isProductImportJob(job) && taxonomyRetrySummary(job)"
          class="taxonomy-retry-line mt-2 small"
          :class="taxonomyRetryLineClass(job)"
        >
          <i class="fas fa-redo-alt me-1"></i>
          {{ taxonomyRetrySummary(job) }}
        </div>
        <div
          v-if="isProductImportJob(job) && taxonomyRetryDetail(job)"
          class="taxonomy-retry-detail small text-muted mt-1"
        >
          {{ taxonomyRetryDetail(job) }}
        </div>

        <div v-if="job.failCount > 0 || (job.errorsSample && job.errorsSample.length > 0)" class="mt-2">
          <button
            class="btn btn-sm btn-link text-danger p-0"
            type="button"
            @click="toggleJobErrors(job.id)"
          >
            <i class="fas fa-exclamation-triangle me-1"></i>
            {{ jobErrorsButtonLabel(job) }}
          </button>
          <button
            v-if="isProductImportJob(job) && job.failCount > 0"
            class="btn btn-sm btn-link text-primary p-0 ms-2"
            type="button"
            @click="downloadJobFailures(job)"
          >
            <i class="fas fa-file-excel me-1"></i>
            Spreadsheet
          </button>
          <div v-if="showErrors[job.id]" class="error-list mt-2 small">
            <div
              v-for="item in jobErrorsDetailed(job)"
              :key="item.key"
              class="import-job-error-item text-danger"
            >
              <div v-if="item.row" class="import-job-error-item__row">{{ item.row }}</div>
              <div v-if="item.sku || item.name" class="import-job-error-item__product">
                <span v-if="item.sku" class="import-job-error-item__sku">SKU {{ item.sku }}</span>
                <span v-if="item.name">{{ item.name }}</span>
              </div>
              <div class="import-job-error-item__message">{{ item.message }}</div>
              <div v-if="item.code" class="import-job-error-item__code text-muted">{{ item.code }}</div>
            </div>
            <p
              v-if="jobErrorsHiddenCount(job) > 0"
              class="text-muted mb-0 mt-2"
            >
              {{ jobErrorsHiddenCount(job) }} more failure(s) not shown
              (latest {{ jobErrorsListedCount(job) }} listed, max {{ IMPORT_JOB_ERRORS_SAMPLE_MAX }}).
              Download the spreadsheet for the full list.
            </p>
          </div>
        </div>
      </div>
    </div>
  </ClMotion>

  <ImportJobFailuresModal
    :visible="!!failuresModal"
    :job-id="failuresModal?.jobId"
    :business-id="failuresModal?.businessId"
    :business-name="failuresModal?.businessName || ''"
    :status="failuresModal?.status || ''"
    :success-count="failuresModal?.successCount || 0"
    :fail-count="failuresModal?.failCount || 0"
    @dismiss="closeFailuresModal"
    @import-again="onFailuresImportAgain"
  />
</template>

<script>
import api from '@/utils/api'
import Swal from 'sweetalert2'
import { withPendingAlert } from '@/utils/alerts'
import { importJobErrorParts, importJobErrorsForDisplay, IMPORT_JOB_ERRORS_SAMPLE_MAX } from '@/utils/importJobErrors'
import ClMotion from '@/components/ds/ClMotion.vue'
import ImportJobFailuresModal from '@/components/shared/ImportJobFailuresModal.vue'

/**
 * Reusable Job Progress Card for Manage Business and Products Management.
 * Shows progress for async jobs: import products, bulk delete, bulk approve, bulk change price (job type bulk_increase_price).
 * Dispatch window 'import-job-started' or 'job-started' with detail: { jobId, businessId?, businessName?, jobTypeCode? }
 * On successful product import, dispatches 'import-job-completed' once with detail:
 *   { jobId, businessId, jobTypeCode, successCount, failCount }
 * Every job type reaching succeeded / failed / cancelled dispatches 'job-finished' once with
 *   { jobId, businessId, jobTypeCode, status, successCount, failCount }.
 * Backend: GET /import-jobs/status/?ids= for every product/bulk bar in one call,
 * GET /import-jobs/<id>/ for a single lookup, POST /import-jobs/<id>/cancel/ to cancel.
 * Polling stops when none of the bars are still queued or running.
 * Product import terminal states with failures open ImportJobFailuresModal (spreadsheet + re-import).
 */
export default {
  name: 'JobProgressCard',
  components: { ClMotion, ImportJobFailuresModal },
  data() {
    return {
      IMPORT_JOB_ERRORS_SAMPLE_MAX,
      activeJobs: [],
      isMinimized: false,
      isDragging: false,
      dragOffset: { x: 0, y: 0 },
      position: { x: 20, y: 20 },
      showErrors: {},
      pollIntervalId: null,
      pollInFlight: false,
      pollAgain: false,
      rafId: null,
      failuresModal: null,
    }
  },
  computed: {
    hasActiveJob() {
      return this.activeJobs.length > 0
    },
    hasActiveRunningJob() {
      return this.activeJobs.some(job => ['queued', 'running'].includes(job.status))
    },
    jobCount() {
      return this.activeJobs.length
    },
    overallPercent() {
      if (this.activeJobs.length === 0) return 0
      const totalPercent = this.activeJobs.reduce((sum, job) => sum + this.getJobPercent(job), 0)
      return totalPercent / this.activeJobs.length
    },
    cardStyle() {
      const baseStyle = {
        position: 'fixed',
        bottom: `${this.position.y}px`,
        right: `${this.position.x}px`,
        zIndex: 9999,
        transform: this.isDragging ? 'translateZ(0)' : '',
        transition: this.isDragging ? 'none' : '',
        willChange: this.isDragging ? 'transform' : 'auto'
      }
      return {
        ...baseStyle,
        width: this.isMinimized ? '250px' : '380px'
      }
    }
  },
  mounted() {
    // Listen before any API work so jobs started during the startup check are not missed.
    window.addEventListener('import-job-started', this.handleJobStarted)
    window.addEventListener('job-started', this.handleJobStarted)
    const savedPosition = localStorage.getItem('importProgressCardPosition')
    if (savedPosition) {
      try {
        this.position = JSON.parse(savedPosition)
      } catch (e) {
        console.error('Failed to parse saved position:', e)
      }
    }
    const savedMinimized = localStorage.getItem('importProgressCardMinimized')
    if (savedMinimized !== null) {
      this.isMinimized = savedMinimized === 'true'
    }
    this.checkForActiveJobs()
      .catch((error) => {
        console.error('[JobProgressCard] Error checking for active jobs on mount:', error)
      })
      .finally(() => {
        if (this.hasActiveRunningJob) this.startPolling()
      })
  },
  beforeUnmount() {
    if (this.pollIntervalId) clearInterval(this.pollIntervalId)
    if (this.rafId) cancelAnimationFrame(this.rafId)
    window.removeEventListener('import-job-started', this.handleJobStarted)
    window.removeEventListener('job-started', this.handleJobStarted)
    document.removeEventListener('mousemove', this.onDrag)
    document.removeEventListener('mouseup', this.stopDrag)
  },
  methods: {
    async fetchJobStatus(job) {
      if (job.jobTypeCode === 'template_broadcast') {
        const statusResp = await api.get(`/admin/template-broadcasts/${job.id}/`)
        const d = statusResp.data || {}
        return {
          status: d.job_status || this.mapBroadcastStatus(d.status),
          processed: d.processed ?? ((d.sent_count || 0) + (d.failed_count || 0) + (d.skipped_count || 0)),
          total: d.total_recipients || 0,
          success_count: d.sent_count || 0,
          fail_count: d.failed_count || 0,
          percent: d.percent || 0,
          errors_sample: [],
          business_name: d.name || job.businessName,
          job_type: { code: 'template_broadcast', name: 'Template campaign' },
        }
      }
      const statusResp = await api.get('/import-jobs/status/', { params: { ids: job.id } })
      const rows = statusResp.data?.jobs || []
      return rows.find((row) => Number(row.id) === Number(job.id)) || {}
    },
    async resolveBusinessName(businessId, { businessName, jobId, jobTypeCode } = {}) {
      if (businessName) return businessName
      if (jobId) {
        try {
          const job = await this.fetchJobStatus({
            id: jobId,
            jobTypeCode: jobTypeCode || 'import',
            businessName,
          })
          if (job.business_name) return job.business_name
        } catch (e) {}
      }
      if (businessId) {
        try {
          const { data } = await api.get(`/businesses/listings/${businessId}/`)
          if (data?.business_name) return data.business_name
        } catch (e) {}
        return `Business ${businessId}`
      }
      return 'Business'
    },
    mapBroadcastStatus(status) {
      const mapping = {
        queued: 'queued',
        sending: 'running',
        completed: 'succeeded',
        failed: 'failed',
        cancelled: 'cancelled',
        scheduled: 'queued',
        draft: 'draft',
      }
      return mapping[status] || status
    },
    async checkForActiveJobs() {
      const keys = Object.keys(localStorage)
      const jobKeys = keys.filter(key => key.startsWith('job_') || key.startsWith('import_job_'))
      const saved = jobKeys.map((key) => this.readSavedJob(key)).filter(Boolean)
      const productJobs = saved.filter((job) => job.jobTypeCode !== 'template_broadcast')
      const broadcasts = saved.filter((job) => job.jobTypeCode === 'template_broadcast')
      if (productJobs.length) {
        try {
          const resp = await api.get('/import-jobs/status/', {
            params: { ids: productJobs.map((job) => job.jobId).join(',') },
          })
          const byId = new Map((resp.data?.jobs || []).map((row) => [Number(row.id), row]))
          productJobs.forEach((savedJob) => {
            const row = byId.get(Number(savedJob.jobId))
            if (!row) {
              localStorage.removeItem(savedJob.key)
              return
            }
            this.adoptSavedJob(savedJob, row)
          })
        } catch (err) {
          console.error('Error checking saved jobs:', err)
        }
      }
      await Promise.all(broadcasts.map((job) => this.restoreSavedJob(job.key)))
    },
    readSavedJob(key) {
      let jobId = null
      let businessId = null
      let jobTypeCode = null
      let businessNameFromStorage = null
      if (key.startsWith('job_')) {
        try {
          const raw = localStorage.getItem(key)
          if (!raw) return null
          const parsed = JSON.parse(raw)
          jobId = parsed?.jobId
          businessId = parsed?.businessId
          jobTypeCode = parsed?.jobTypeCode
          businessNameFromStorage = parsed?.businessName
        } catch (e) {
          localStorage.removeItem(key)
          return null
        }
      } else {
        jobId = localStorage.getItem(key)
        businessId = key.replace('import_job_', '')
        jobTypeCode = 'import'
      }
      if (!jobId) return null
      return { key, jobId, businessId, jobTypeCode, businessNameFromStorage }
    },
    adoptSavedJob(savedJob, job) {
      const { key, jobId, businessId, jobTypeCode, businessNameFromStorage } = savedJob
      let businessName = job.business_name || businessNameFromStorage
      if (!businessName) {
        businessName = businessId ? `Business ${businessId}` : 'Business'
      }
      if (['queued', 'running'].includes(job.status)) {
        const apiJobType = job.job_type || {}
        this.addOrUpdateJob({
          id: parseInt(jobId, 10),
          businessId,
          businessName,
          jobTypeCode: apiJobType.code || jobTypeCode,
          jobTypeName: apiJobType.name || (jobTypeCode === 'template_broadcast' ? 'Template campaign' : null),
          status: job.status,
          processed: job.processed || 0,
          total: job.total || 0,
          successCount: job.success_count || 0,
          failCount: job.fail_count || 0,
          percent: job.percent || 0,
          errorsSample: job.errors_sample || [],
          progressMeta: job.progress_meta || {}
        })
      } else if (!this.activeJobs.some((j) => String(j.id) === String(jobId))) {
        localStorage.removeItem(key)
      }
    },
    async restoreSavedJob(key) {
      let jobId = null
      let businessId = null
      let jobTypeCode = null
      let businessNameFromStorage = null
      if (key.startsWith('job_')) {
        try {
          const raw = localStorage.getItem(key)
          if (!raw) return
          const parsed = JSON.parse(raw)
          jobId = parsed?.jobId
          businessId = parsed?.businessId
          jobTypeCode = parsed?.jobTypeCode
          businessNameFromStorage = parsed?.businessName
        } catch (e) {
          localStorage.removeItem(key)
          return
        }
      } else {
        jobId = localStorage.getItem(key)
        businessId = key.replace('import_job_', '')
        jobTypeCode = 'import'
      }
      if (!jobId) return
      try {
        const job = await this.fetchJobStatus({ id: jobId, jobTypeCode, businessName: businessNameFromStorage })
        let businessName = job.business_name || businessNameFromStorage
        if (jobTypeCode !== 'template_broadcast' && !businessName) {
          businessName = await this.resolveBusinessName(businessId, {
            businessName: businessNameFromStorage,
            jobId,
            jobTypeCode,
          })
        } else if (!businessName) {
          businessName = `Business ${businessId || 'Unknown'}`
        }
        if (['queued', 'running'].includes(job.status)) {
          const apiJobType = job.job_type || {}
          this.addOrUpdateJob({
            id: parseInt(jobId),
            businessId,
            businessName,
            jobTypeCode: apiJobType.code || jobTypeCode,
            jobTypeName: apiJobType.name || (jobTypeCode === 'template_broadcast' ? 'Template campaign' : null),
            status: job.status,
            processed: job.processed || 0,
            total: job.total || 0,
            successCount: job.success_count || 0,
            failCount: job.fail_count || 0,
            percent: job.percent || 0,
            errorsSample: job.errors_sample || [],
            progressMeta: job.progress_meta || {}
          })
          this.startPolling()
        } else if (!this.activeJobs.some((j) => String(j.id) === String(jobId))) {
          localStorage.removeItem(key)
        }
      } catch (err) {
        console.error('Error checking job:', err)
        localStorage.removeItem(key)
      }
    },
    async handleJobStarted(event) {
      const { jobId, businessId, businessName, jobTypeCode } = event.detail || {}
      const finalBusinessName = jobTypeCode === 'template_broadcast'
        ? (businessName || 'Template campaign')
        : await this.resolveBusinessName(businessId, { businessName, jobId, jobTypeCode })
      this.addOrUpdateJob({
        id: jobId,
        businessId,
        businessName: finalBusinessName,
        jobTypeCode: jobTypeCode || 'import',
        jobTypeName: jobTypeCode === 'template_broadcast' ? 'Template campaign' : null,
        status: 'queued',
        processed: 0,
        total: 0,
        successCount: 0,
        failCount: 0,
        percent: 0,
        errorsSample: [],
        progressMeta: {}
      })
      try {
        localStorage.setItem(`job_${jobId}`, JSON.stringify({
          jobId,
          businessId,
          businessName: finalBusinessName,
          jobTypeCode: jobTypeCode || 'import'
        }))
      } catch (e) {}
      this.startPolling()
      this.$nextTick(() => {
        window.dispatchEvent(new CustomEvent('import-progress-card-visible', { detail: { jobId, businessId } }))
      })
    },
    toggleJobErrors(jobId) {
      this.showErrors = { ...this.showErrors, [jobId]: !this.showErrors[jobId] }
    },
    jobErrorsButtonLabel(job) {
      const shown = this.jobErrorsListedCount(job)
      const failed = job.failCount || 0
      if (failed > 0 && shown > 0) return `Failed: ${failed} (${shown} detailed)`
      if (failed > 0) return `Failed: ${failed}`
      return `${shown} error(s)`
    },
    jobErrorsListedCount(job) {
      return importJobErrorsForDisplay(job.errorsSample).length
    },
    jobErrorsHiddenCount(job) {
      const failed = job.failCount || 0
      const listed = this.jobErrorsListedCount(job)
      return Math.max(0, failed - listed)
    },
    jobErrorsDetailed(job) {
      return importJobErrorsForDisplay(job.errorsSample).map((error, idx) => {
        const parts = importJobErrorParts(error)
        return {
          key: `${job.id}-${idx}-${error?.index ?? ''}-${parts.sku}-${parts.message?.slice(0, 40)}`,
          ...parts,
        }
      })
    },
    getJobPercent(job) {
      if (job.percent !== undefined && job.percent !== null) return Number(job.percent)
      if (job.total && job.total > 0 && job.processed != null) return (job.processed / job.total) * 100
      return 0
    },
    taxonomyStats(job) {
      const meta = job?.progressMeta || {}
      return meta.taxonomy && typeof meta.taxonomy === 'object' ? meta.taxonomy : null
    },
    taxonomyRetrySummary(job) {
      const t = this.taxonomyStats(job)
      if (!t) return ''
      const needed = Number(t.retry_needed) || 0
      const recovered = Number(t.recovered) || 0
      const still = Number(t.still_unresolved) || 0
      if (needed <= 0 && recovered <= 0 && still <= 0) return ''
      return `Taxonomy retries: ${needed} tried · ${recovered} recovered · ${still} still failed`
    },
    taxonomyRetryDetail(job) {
      const t = this.taxonomyStats(job)
      if (!t) return ''
      const parts = []
      const imgA = Number(t.image_attempted) || 0
      const imgR = Number(t.image_recovered) || 0
      const enA = Number(t.enriched_attempted) || 0
      const enR = Number(t.enriched_recovered) || 0
      if (imgA > 0) parts.push(`image ${imgR}/${imgA} passed`)
      if (enA > 0) parts.push(`SKU text ${enR}/${enA} passed`)
      const hierA = Number(t.hierarchy_attempted) || 0
      const hierR = Number(t.hierarchy_recovered) || 0
      if (hierA > 0) parts.push(`name hierarchy ${hierR}/${hierA} passed`)
      const text = Number(t.text_resolved) || 0
      const reuse = Number(t.db_reuse) || 0
      if (text > 0 || reuse > 0) {
        parts.push(`first pass: ${reuse} reused, ${text} from name`)
      }
      return parts.join(' · ')
    },
    taxonomyRetryLineClass(job) {
      const t = this.taxonomyStats(job)
      if (!t) return 'text-muted'
      const still = Number(t.still_unresolved) || 0
      const recovered = Number(t.recovered) || 0
      if (still > 0 && recovered > 0) return 'text-warning'
      if (still > 0) return 'text-danger'
      if (recovered > 0) return 'text-success'
      return 'text-muted'
    },
    addOrUpdateJob(jobData) {
      const index = this.activeJobs.findIndex(j => j.id === jobData.id)
      const existing = index >= 0 ? this.activeJobs[index] : null
      const updatedJob = {
        id: jobData.id,
        businessId: jobData.businessId,
        businessName: jobData.businessName,
        jobTypeCode: jobData.jobTypeCode,
        jobTypeName: jobData.jobTypeName,
        status: jobData.status,
        processed: Number(jobData.processed) || 0,
        total: Number(jobData.total) || 0,
        successCount: Number(jobData.successCount) || 0,
        failCount: Number(jobData.failCount) || 0,
        percent: Number(jobData.percent) || 0,
        errorsSample: jobData.errorsSample || [],
        progressMeta: jobData.progressMeta || existing?.progressMeta || {},
        // Preserve one-shot guards across poll updates
        completionNotified: existing?.completionNotified || false,
        removalScheduled: existing?.removalScheduled || false,
        failuresModalShown: existing?.failuresModalShown || false,
      }
      if (index >= 0) {
        this.activeJobs[index] = updatedJob
      } else {
        this.activeJobs.push(updatedJob)
      }
    },
    /** Any job type reaching a terminal state: window 'job-finished' once (pages refresh their lists). */
    notifyJobFinished(job) {
      if (!job || job.finishNotified) return
      job.finishNotified = true
      window.dispatchEvent(new CustomEvent('job-finished', {
        detail: {
          jobId: job.id,
          businessId: job.businessId,
          jobTypeCode: job.jobTypeCode || 'import',
          status: job.status,
          successCount: job.successCount,
          failCount: job.failCount
        }
      }))
    },
    notifyImportJobCompleted(job) {
      if (!job || job.completionNotified) return
      const code = job.jobTypeCode || 'import'
      // Product import jobs only (legacy jobs may omit type and default to import)
      if (code !== 'import') return
      if (job.status !== 'succeeded') return
      if (!(Number(job.successCount) > 0)) return
      job.completionNotified = true
      window.dispatchEvent(new CustomEvent('import-job-completed', {
        detail: {
          jobId: job.id,
          businessId: job.businessId,
          jobTypeCode: code,
          successCount: job.successCount,
          failCount: job.failCount
        }
      }))
    },
    isProductImportJob(job) {
      const code = (job?.jobTypeCode || 'import').toLowerCase()
      return code === 'import' || code === 'product_import'
    },
    maybeOpenFailuresModal(job) {
      if (!job || job.failuresModalShown) return
      if (!this.isProductImportJob(job)) return
      if (!['succeeded', 'failed', 'cancelled'].includes(job.status)) return
      if (!(Number(job.failCount) > 0)) return
      job.failuresModalShown = true
      this.failuresModal = {
        jobId: job.id,
        businessId: job.businessId,
        businessName: job.businessName,
        status: job.status,
        successCount: Number(job.successCount) || 0,
        failCount: Number(job.failCount) || 0,
      }
    },
    closeFailuresModal() {
      const jobId = this.failuresModal?.jobId
      this.failuresModal = null
      if (jobId != null) this.scheduleJobRemoval(jobId)
    },
    onFailuresImportAgain({ businessId, businessName }) {
      const jobId = this.failuresModal?.jobId
      this.failuresModal = null
      if (jobId != null) this.scheduleJobRemoval(jobId, 0)
      window.dispatchEvent(
        new CustomEvent('open-import-products', {
          detail: { businessId, businessName },
        })
      )
    },
    async downloadJobFailures(job) {
      if (!job?.id) return
      try {
        const resp = await api.get(`/import-jobs/${job.id}/failures/export/`, {
          responseType: 'blob',
        })
        const raw = resp.data
        const blob =
          raw instanceof Blob
            ? raw
            : new Blob([raw], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              })
        const head = new Uint8Array(await blob.slice(0, 2).arrayBuffer())
        if (head.length < 2 || head[0] !== 0x50 || head[1] !== 0x4b) {
          let detail = 'Download was not a valid Excel file.'
          try {
            detail = (await blob.text()).slice(0, 200) || detail
          } catch (_) {
            /* ignore */
          }
          throw new Error(detail)
        }
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        const disposition = resp.headers?.get?.('content-disposition') || ''
        const match = /filename="?([^"]+)"?/i.exec(disposition)
        a.href = url
        a.download = match?.[1] || `import_job_${job.id}_failures.xlsx`
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        console.error('Failed to download import failures', e)
        await Swal.fire({
          icon: 'error',
          title: 'Download failed',
          text: e.response?.data?.error || e.message || 'Could not download failures spreadsheet.',
        })
      }
    },
    scheduleJobRemoval(jobId, delayMs = 5000) {
      const jobInActiveJobs = this.activeJobs.find((j) => j.id === jobId)
      if (!jobInActiveJobs || jobInActiveJobs.removalScheduled) return
      jobInActiveJobs.removalScheduled = true
      setTimeout(() => {
        const jobIndex = this.activeJobs.findIndex((j) => j.id === jobId)
        if (jobIndex >= 0) {
          const removed = this.activeJobs[jobIndex]
          this.activeJobs.splice(jobIndex, 1)
          localStorage.removeItem(`job_${jobId}`)
          if (removed?.businessId != null) {
            localStorage.removeItem(`import_job_${removed.businessId}`)
          }
          if (this.activeJobs.length === 0 && this.pollIntervalId) {
            clearInterval(this.pollIntervalId)
            this.pollIntervalId = null
          }
        }
      }, delayMs)
    },
    stopPolling() {
      if (!this.pollIntervalId) return
      clearInterval(this.pollIntervalId)
      this.pollIntervalId = null
    },
    async startPolling() {
      if (this.pollIntervalId) clearInterval(this.pollIntervalId)
      // Timer first so overlapping startPolling calls never orphan an interval.
      this.pollIntervalId = setInterval(this.pollActiveJobs, 5000)
      await this.pollActiveJobs()
    },
    async pollActiveJobs() {
      // A job that starts while a fetch is in flight is included in the next pass,
      // still one request for every product bar, not one request per bar.
      if (this.pollInFlight) {
        this.pollAgain = true
        return
      }
      this.pollInFlight = true
      try {
        do {
          this.pollAgain = false
          await this.pollActiveJobsOnce()
        } while (this.pollAgain)
      } finally {
        this.pollInFlight = false
      }
    },
    async pollActiveJobsOnce() {
        const activeJobsToPoll = this.activeJobs.filter(job =>
          !['succeeded', 'failed', 'cancelled'].includes(job.status)
        )
        if (activeJobsToPoll.length === 0) {
          this.stopPolling()
          return
        }
        const productJobs = activeJobsToPoll.filter((job) => job.jobTypeCode !== 'template_broadcast')
        const broadcastJobs = activeJobsToPoll.filter((job) => job.jobTypeCode === 'template_broadcast')
        if (productJobs.length) {
          try {
            const resp = await api.get('/import-jobs/status/', {
              params: { ids: productJobs.map((job) => job.id).join(',') },
            })
            const byId = new Map((resp.data?.jobs || []).map((row) => [Number(row.id), row]))
            productJobs.forEach((job) => {
              const jobData = byId.get(Number(job.id))
              if (!jobData) {
                console.error('Polling error for job:', job.id, 'not in the batch response')
                return
              }
              this.applyPolledJob(job, jobData)
            })
          } catch (err) {
            console.error('Polling error for product jobs:', err)
          }
        }
        for (const job of broadcastJobs) {
          try {
            const jobData = await this.fetchJobStatus(job)
            this.applyPolledJob(job, jobData)
          } catch (err) {
            console.error('Polling error for job:', job.id, err)
          }
        }
        const stillRunning = this.activeJobs.some((job) => ['queued', 'running'].includes(job.status))
        if (!stillRunning) this.stopPolling()
    },
    applyPolledJob(job, jobData) {
            const processed = jobData.processed ?? 0
            const total = jobData.total ?? 0
            const successCount = jobData.success_count ?? 0
            const failCount = jobData.fail_count ?? 0
            let percent = 0
            if (jobData.percent != null) percent = Number(jobData.percent)
            else if (total > 0 && processed > 0) percent = (processed / total) * 100
            let businessName = jobData.business_name || job.businessName
            if (!businessName) {
              businessName = job.jobTypeCode === 'template_broadcast'
                ? 'Template campaign'
                : (job.businessId ? `Business ${job.businessId}` : 'Business')
            }
            const apiJobType = jobData.job_type || {}
            this.addOrUpdateJob({
              id: job.id,
              businessId: job.businessId,
              businessName,
              jobTypeCode: apiJobType.code || job.jobTypeCode,
              jobTypeName: apiJobType.name || job.jobTypeName || (job.jobTypeCode === 'template_broadcast' ? 'Template campaign' : null),
              status: jobData.status || job.status,
              processed,
              total,
              successCount,
              failCount,
              percent,
              errorsSample: jobData.errors_sample || [],
              progressMeta: jobData.progress_meta || {}
            })
            try {
              localStorage.setItem(`job_${job.id}`, JSON.stringify({
                jobId: job.id,
                businessId: job.businessId,
                businessName,
                jobTypeCode: apiJobType.code || job.jobTypeCode || 'import'
              }))
            } catch (e) {}
            if (['succeeded', 'failed', 'cancelled'].includes(jobData.status)) {
              const jobInActiveJobs = this.activeJobs.find(j => j.id === job.id)
              if (jobInActiveJobs) {
                this.notifyImportJobCompleted(jobInActiveJobs)
                this.notifyJobFinished(jobInActiveJobs)
                this.maybeOpenFailuresModal(jobInActiveJobs)
                // Keep card until failures modal is dismissed when failures exist.
                if (
                  this.isProductImportJob(jobInActiveJobs) &&
                  Number(jobInActiveJobs.failCount) > 0
                ) {
                  /* removal scheduled from closeFailuresModal */
                } else {
                  this.scheduleJobRemoval(job.id)
                }
              }
            }
    },
    async removeJob(jobId) {
      const job = this.activeJobs.find(j => j.id === jobId)
      if (!job) return
      if (['succeeded', 'failed', 'cancelled'].includes(job.status)) {
        localStorage.removeItem(`job_${job.id}`)
        localStorage.removeItem(`import_job_${job.businessId}`)
        this.activeJobs = this.activeJobs.filter(j => j.id !== jobId)
        if (this.activeJobs.length === 0 && this.pollIntervalId) {
          clearInterval(this.pollIntervalId)
          this.pollIntervalId = null
        }
        return
      }
      const result = await Swal.fire({
        title: 'Cancel job?',
        html: `
          <p>Are you sure you want to cancel this job for <strong>${job.businessName || 'this business'}</strong>?</p>
          <p class="text-warning mt-2"><strong>This action cannot be undone!</strong></p>
          <p class="mt-2">Progress: ${job.processed} / ${job.total} (${Math.round(job.percent)}%)</p>
          ${job.jobTypeCode === 'template_broadcast' ? '<p class="mt-2 small">Messages already sent will remain sent.</p>' : ''}
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, cancel it!',
        cancelButtonText: 'No, keep it running',
        reverseButtons: true
      })
      if (!result.isConfirmed) return
      try {
        await withPendingAlert(async () => {
          const cancelUrl = job.jobTypeCode === 'template_broadcast'
            ? `/admin/template-broadcasts/${jobId}/cancel/`
            : `/import-jobs/${jobId}/cancel/`
          const response = await api.post(cancelUrl, {})
          if (response.data && (response.data.ok || response.data.status || response.data.broadcast)) {
            this.addOrUpdateJob({
              ...job,
              status: 'cancelled',
              processed: job.processed,
              total: job.total,
              successCount: job.successCount,
              failCount: job.failCount,
              percent: job.percent,
              errorsSample: job.errorsSample || [],
              progressMeta: job.progressMeta || {},
            })
            const updated = this.activeJobs.find((j) => j.id === jobId)
            if (updated) {
              this.maybeOpenFailuresModal(updated)
              if (
                this.isProductImportJob(updated) &&
                Number(updated.failCount) > 0
              ) {
                Swal.fire({
                  title: 'Cancelled',
                  text: 'The job was cancelled. Review failed products in the modal.',
                  icon: 'info',
                })
              } else {
                localStorage.removeItem(`job_${job.id}`)
                localStorage.removeItem(`import_job_${job.businessId}`)
                this.activeJobs = this.activeJobs.filter((j) => j.id !== jobId)
                Swal.fire({ title: 'Cancelled!', text: 'The job has been cancelled.', icon: 'success' })
                if (this.activeJobs.length === 0 && this.pollIntervalId) {
                  clearInterval(this.pollIntervalId)
                  this.pollIntervalId = null
                }
              }
            }
          } else {
            throw new Error(response.data?.error || response.data?.detail || 'Failed to cancel job')
          }
        }, 'Cancelling job…')
      } catch (error) {
        console.error('Error cancelling job:', error)
        Swal.fire({
          title: 'Error',
          text: error.response?.data?.error || error.response?.data?.detail || error.message || 'Failed to cancel the job. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        })
      }
    },
    toggleMinimize() {
      const wasMinimized = this.isMinimized
      this.isMinimized = !this.isMinimized
      if (wasMinimized && !this.isMinimized) {
        const cardWidth = 380
        const cardHeight = 300
        const minimizedWidth = 250
        const minimizedHeight = 50
        const currentLeft = window.innerWidth - this.position.x - minimizedWidth
        const currentTop = window.innerHeight - this.position.y - minimizedHeight
        const constrainedLeft = Math.max(0, Math.min(currentLeft, window.innerWidth - cardWidth))
        const constrainedTop = Math.max(0, Math.min(currentTop, window.innerHeight - cardHeight))
        this.position = {
          x: window.innerWidth - constrainedLeft - cardWidth,
          y: window.innerHeight - constrainedTop - cardHeight
        }
      }
      localStorage.setItem('importProgressCardMinimized', String(this.isMinimized))
      localStorage.setItem('importProgressCardPosition', JSON.stringify(this.position))
    },
    getCardElement(fromEvent) {
      const el = fromEvent?.currentTarget?.closest?.('.job-progress-card')
      return el instanceof Element ? el : null
    },
    startDrag(e) {
      if (!e.target.closest('.card-header') || e.target.closest('.card-controls') || e.target.closest('button')) return
      const cardEl = this.getCardElement(e)
      if (!cardEl) return
      this.isDragging = true
      const rect = cardEl.getBoundingClientRect()
      this.dragOffset = { x: e.clientX - rect.left, y: e.clientY - rect.top }
      this.rafId = null
      document.addEventListener('mousemove', this.onDrag)
      document.addEventListener('mouseup', this.stopDrag)
      e.preventDefault()
      e.stopPropagation()
    },
    onDrag(e) {
      if (!this.isDragging) return
      if (this.rafId) cancelAnimationFrame(this.rafId)
      this.rafId = requestAnimationFrame(() => {
        const cardWidth = this.isMinimized ? 250 : 380
        const cardHeight = this.isMinimized ? 50 : 300
        const newLeft = e.clientX - this.dragOffset.x
        const newTop = e.clientY - this.dragOffset.y
        const constrainedLeft = Math.max(0, Math.min(newLeft, window.innerWidth - cardWidth))
        const constrainedTop = Math.max(0, Math.min(newTop, window.innerHeight - cardHeight))
        this.position = {
          x: window.innerWidth - constrainedLeft - cardWidth,
          y: window.innerHeight - constrainedTop - cardHeight
        }
      })
    },
    stopDrag() {
      this.isDragging = false
      if (this.rafId) {
        cancelAnimationFrame(this.rafId)
        this.rafId = null
      }
      document.removeEventListener('mousemove', this.onDrag)
      document.removeEventListener('mouseup', this.stopDrag)
      localStorage.setItem('importProgressCardPosition', JSON.stringify(this.position))
    }
  }
}
</script>

<style scoped>
.job-progress-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  overflow: hidden;
  user-select: none;
}
.job-progress-card.minimized { height: auto; }
.job-progress-card:not(.minimized) { max-height: 500px; overflow-y: auto; }
.card-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 12px 16px;
  cursor: move;
  user-select: none;
}
.card-header:hover { background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%); }
.card-controls { display: flex; gap: 4px; }
.card-controls .btn-link {
  color: rgba(255, 255, 255, 0.9) !important;
  text-decoration: none;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background 0.2s;
}
.card-controls .btn-link:hover { background: rgba(255, 255, 255, 0.2); color: white !important; }
.card-body { padding: 16px; background: white; }
.job-item {
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 3px solid #667eea;
}
.job-item:last-child { margin-bottom: 0 !important; }
.progress { border-radius: 10px; overflow: hidden; }
.progress-bar {
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}
.error-list {
  background: #fff5f5;
  border: 1px solid #fed7d7;
  border-radius: 4px;
  padding: 8px;
  max-height: min(70vh, 520px);
  overflow-y: auto;
}
.import-job-error-item {
  padding: 6px 0;
  border-bottom: 1px solid #fed7d7;
}
.import-job-error-item:last-child {
  border-bottom: none;
}
.import-job-error-item__row {
  font-weight: 600;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.import-job-error-item__product {
  margin-top: 2px;
  color: #742a2a;
}
.import-job-error-item__sku {
  font-weight: 600;
  margin-right: 0.35rem;
}
.import-job-error-item__message {
  margin-top: 4px;
  color: #c53030;
}
.import-job-error-item__code {
  margin-top: 2px;
  font-size: 0.65rem;
}
.taxonomy-retry-line {
  font-weight: 600;
  line-height: 1.3;
}
.taxonomy-retry-detail {
  line-height: 1.3;
}
.job-progress-card::-webkit-scrollbar { width: 6px; }
.job-progress-card::-webkit-scrollbar-track { background: #f1f1f1; }
.job-progress-card::-webkit-scrollbar-thumb { background: #888; border-radius: 3px; }
.job-progress-card::-webkit-scrollbar-thumb:hover { background: #555; }
@media (max-width: 768px) {
  .job-progress-card {
    width: calc(100vw - 40px) !important;
    right: 20px !important;
    left: 20px !important;
  }
}
</style>
